import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import image from "../../../src/assests/profile.jpg"
import { useSelector, useDispatch } from "react-redux";
import { fetchAllBranchSettings, updateBranchSetting } from "../../store/businessSlice";
import Swal from "sweetalert2";
import { baseurImage } from "../../Baseurl";
import { getCurrentUserId } from "../../utils/functions";

const COLORS = {
  gray: '#5B6B79',
  blue: '#96C5F7',
  red: '#9E2B25',
  orange: '#F29E4C',
  green: '#2CA87F',
};

const EMPTY_FORM = {
  official_name: "",
  official_name_arabic: "",
  cr_number: "",
  telephone_1: "",
  telephone_2: "",
  vat_number: "",
  email: "",
  branches: "1",
  specialty: "",
  address: "",
  website: "",
};

const fieldStyle = {
  height: 36, borderRadius: 8, border: '1px solid #dde3eb',
  padding: '0 12px', fontSize: 13, color: '#1a202c', outline: 'none', width: '100%',
  background: '#fff',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 };

const BusinessSetting = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { allBranchSettings, loading } = useSelector((state) => state.business);

  const doctorId = getCurrentUserId();

  // activeIdx = index in allBranchSettings
  const [activeIdx, setActiveIdx] = useState(0);
  // branchForms keyed by branch id
  const [branchForms, setBranchForms] = useState({});
  // logoFiles keyed by branch id
  const [logoFiles, setLogoFiles] = useState({});
  const logoInputRef = useRef(null);

  // Fetch all branch settings on mount
  useEffect(() => {
    if (doctorId) {
      dispatch(fetchAllBranchSettings(doctorId));
    }
  }, [dispatch, doctorId]);

  // When allBranchSettings loads/changes, initialize branchForms
  useEffect(() => {
    if (allBranchSettings && allBranchSettings.length > 0) {
      const forms = {};
      allBranchSettings.forEach(branch => {
        if (!branchForms[branch.id]) {
          forms[branch.id] = {
            official_name: branch.official_name || "",
            official_name_arabic: branch.official_name_arabic || "",
            cr_number: branch.cr_number || "",
            telephone_1: branch.telephone_1 || "",
            telephone_2: branch.telephone_2 || "",
            vat_number: branch.vat_number || "",
            email: branch.email || "",
            branches: branch.branches != null ? String(branch.branches) : "1",
            specialty: branch.specialty || "",
            address: branch.address || "",
            website: branch.website || "",
          };
        }
      });
      if (Object.keys(forms).length > 0) {
        setBranchForms(prev => ({ ...forms, ...prev }));
      }
    }
  }, [allBranchSettings]);

  const activeBranch = allBranchSettings?.[activeIdx];
  const activeForm = activeBranch ? (branchForms[activeBranch.id] || EMPTY_FORM) : EMPTY_FORM;

  const handleChange = (e) => {
    if (!activeBranch) return;
    let { name, value, type, checked } = e.target;
    if (type === "checkbox") value = checked ? 1 : 0;
    setBranchForms(prev => ({
      ...prev,
      [activeBranch.id]: { ...prev[activeBranch.id], [name]: value }
    }));
  };

  const handleBranchCount = (delta) => {
    if (!activeBranch) return;
    const current = parseInt(activeForm.branches) || 1;
    const next = Math.max(1, Math.min(10, current + delta));
    setBranchForms(prev => ({
      ...prev,
      [activeBranch.id]: { ...prev[activeBranch.id], branches: String(next) }
    }));
  };

  const handleLogoChange = (e) => {
    if (!activeBranch) return;
    const file = e.target.files[0];
    if (!file) return;
    const isPNG = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPNG) {
      Swal.fire({ icon: 'error', title: 'Invalid File Type', text: 'Only PNG format is allowed for the business logo.', confirmButtonText: 'OK' });
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: 'error', title: 'File Too Large', text: 'Image size should not exceed 5MB.', confirmButtonText: 'OK' });
      e.target.value = '';
      return;
    }
    setLogoFiles(prev => ({ ...prev, [activeBranch.id]: file }));
  };

  const triggerLogoPick = () => { if (logoInputRef.current) logoInputRef.current.click(); };

  const getLogoPreview = () => {
    if (!activeBranch) return image;
    const logoFile = logoFiles[activeBranch.id];
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return url;
    }
    const logoFromServer = activeBranch?.image || activeBranch?.logo;
    if (logoFromServer) {
      if (logoFromServer.startsWith("http://") || logoFromServer.startsWith("https://")) return logoFromServer;
      return `${baseurImage}${logoFromServer}?v=${new Date().getTime()}`;
    }
    return image;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeBranch) return;

    try {
      await dispatch(updateBranchSetting({
        branchId: activeBranch.id,
        formData: activeForm,
        logoFile: logoFiles[activeBranch.id] || null,
        doctorId,
        isPrimary: activeIdx === 0,
      })).unwrap();

      // Clear logo file state for this branch after save
      setLogoFiles(prev => { const n = { ...prev }; delete n[activeBranch.id]; return n; });

      await Swal.fire({ icon: 'success', title: 'Success!', text: `Branch ${activeIdx + 1} settings updated successfully!`, timer: 2000, showConfirmButton: false });

    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Error!', text: error || 'Failed to save branch settings. Please try again.', confirmButtonText: 'OK' });
    }
  };

  const branches = allBranchSettings?.length > 0 ? allBranchSettings : null;

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray; }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-building" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Business Settings</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage your clinic's official information per branch</div>
            </div>
          </div>

          {/* Branch Tabs */}
          {branches && branches.length > 1 && (
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e8ecf0', background: '#fafbfc', padding: '0 20px' }}>
              {branches.map((branch, idx) => (
                <button
                  key={branch.id}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  style={{
                    padding: '10px 18px', fontSize: 13, fontWeight: activeIdx === idx ? 700 : 500,
                    color: activeIdx === idx ? COLORS.blue : COLORS.gray,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    borderBottom: activeIdx === idx ? `2px solid ${COLORS.blue}` : '2px solid transparent',
                    marginBottom: -1, transition: 'all 0.15s',
                  }}
                >
                  <i className="ti ti-building-store" style={{ fontSize: 12, marginRight: 5 }} />
                  Branch {idx + 1}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div style={{ padding: '20px' }}>

              {/* Logo Section */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '14px 16px', borderRadius: 10, background: '#fafbfc',
                border: '1px solid #e8ecf0', marginBottom: 20,
              }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={getLogoPreview()}
                    alt="Business Logo"
                    style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', border: '2px solid #e8ecf0' }}
                  />
                  <button
                    type="button"
                    onClick={triggerLogoPick}
                    title="Change logo"
                    style={{
                      position: 'absolute', bottom: -6, right: -6,
                      width: 22, height: 22, borderRadius: 6, border: '2px solid #fff',
                      background: COLORS.blue, color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                    }}
                  >
                    <i className="ti ti-edit" />
                  </button>
                  <input ref={logoInputRef} type="file" onChange={handleLogoChange} className="d-none" accept="image/png" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a202c' }}>
                    {branches && branches.length > 1 ? `Branch ${activeIdx + 1} Logo` : 'Business Logo'}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 2 }}>PNG format only · Max 5 MB</div>
                  <button
                    type="button"
                    onClick={triggerLogoPick}
                    style={{
                      marginTop: 6, fontSize: 11, padding: '3px 10px', borderRadius: 6,
                      border: `1px solid ${COLORS.blue}`, background: `${COLORS.blue}15`,
                      color: COLORS.blue, cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    Upload Logo
                  </button>
                </div>
              </div>

              {/* Fields Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>

                <div>
                  <label style={labelStyle}>Official Name (English)</label>
                  <input type="text" style={fieldStyle} name="official_name" value={activeForm.official_name} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Official Name (Arabic)</label>
                  <input type="text" style={{ ...fieldStyle, direction: 'rtl', textAlign: 'right' }} name="official_name_arabic" value={activeForm.official_name_arabic} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>CR Number</label>
                  <input type="text" style={fieldStyle} name="cr_number" value={activeForm.cr_number} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>VAT Number</label>
                  <input type="text" style={fieldStyle} name="vat_number" value={activeForm.vat_number} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Telephone 1</label>
                  <input type="text" style={fieldStyle} name="telephone_1" value={activeForm.telephone_1} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Telephone 2</label>
                  <input type="text" style={fieldStyle} name="telephone_2" value={activeForm.telephone_2} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" style={fieldStyle} name="email" value={activeForm.email} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Specialty</label>
                  <input type="text" style={fieldStyle} name="specialty" value={activeForm.specialty} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Address</label>
                  <input type="text" style={fieldStyle} name="address" value={activeForm.address} onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                <div>
                  <label style={labelStyle}>Website</label>
                  <input type="text" style={fieldStyle} name="website" value={activeForm.website} placeholder="https://example.com" onChange={handleChange}
                    onFocus={e => e.target.style.borderColor = COLORS.blue}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                </div>

                {/* Branches counter — only on Branch 1 (primary) */}
                {activeIdx === 0 && (
                  <div>
                    <label style={labelStyle}>Branches</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => handleBranchCount(-1)}
                        disabled={parseInt(activeForm.branches) <= 1}
                        style={{
                          width: 32, height: 36, borderRadius: 8, border: '1px solid #dde3eb',
                          background: parseInt(activeForm.branches) <= 1 ? '#f8f9fb' : '#fff',
                          color: parseInt(activeForm.branches) <= 1 ? '#ccc' : COLORS.gray,
                          cursor: parseInt(activeForm.branches) <= 1 ? 'default' : 'pointer',
                          fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                      >−</button>
                      <div style={{
                        flex: 1, height: 36, borderRadius: 8, border: '1px solid #dde3eb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, color: '#1a202c', background: '#fff',
                      }}>
                        {activeForm.branches || '1'}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleBranchCount(1)}
                        disabled={parseInt(activeForm.branches) >= 10}
                        style={{
                          width: 32, height: 36, borderRadius: 8, border: '1px solid #dde3eb',
                          background: parseInt(activeForm.branches) >= 10 ? '#f8f9fb' : '#fff',
                          color: parseInt(activeForm.branches) >= 10 ? '#ccc' : COLORS.gray,
                          cursor: parseInt(activeForm.branches) >= 10 ? 'default' : 'pointer',
                          fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                      >+</button>
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.gray, marginTop: 4 }}>
                      Saving will add or remove branch tabs accordingly
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px', borderTop: '1px solid #e8ecf0',
              background: '#fafbfc', display: 'flex', justifyContent: 'flex-end',
            }}>
              <button type="submit" className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '6px 20px' }} disabled={loading}>
                {loading && <i className="ti ti-loader-2" style={{ fontSize: 14 }} />}
                {loading ? 'Saving...' : `Save ${branches && branches.length > 1 ? `Branch ${activeIdx + 1} ` : ''}Changes`}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default BusinessSetting;
